import { useAuth } from '../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { investorRolesAPI } from '../services/api';
import { custodianRolesAPI } from '../services/custodianRolesAPI';
import { facilityAgentRolesAPI } from '../services/facilityAgentRolesAPI';
import { issuerRolesAPI } from '../services/issuerRolesAPI';
import { superAdminRolesAPI } from '../services/superAdminRolesAPI';
import { trusteeRolesAPI } from '../services/trusteeRolesAPI';
import { primarySubscriberRolesAPI } from '../services/primarySubscriberRolesAPI';

// =============================================================================
// SIMPLIFIED SIDEBAR-BASED PERMISSIONS
// =============================================================================
// These permissions control access to sidebar menu items and their associated pages.
// Settings page is always accessible (no permission required).
// Role/SubAccount Management is accessible to both admin and sub-accounts,
// but only admin can CREATE roles/sub-accounts.
// =============================================================================

// Issuer Permissions
export const ISSUER_PERMISSIONS = {
  ACCESS_DASHBOARD: 'access_dashboard',
  ACCESS_CREATE_BOND: 'access_create_bond',
  ACCESS_BOND_MANAGEMENT: 'access_bond_management',
  ACCESS_DOCUMENTS: 'access_documents',
  ACCESS_ANNOUNCEMENTS: 'access_announcements',
  ACCESS_SUB_ACCOUNTS: 'access_sub_accounts',
  ACCESS_ROLE_MANAGEMENT: 'access_role_management',
  ACCESS_AUDIT_LOGS: 'access_audit_logs'
};

// Facility Agent Permissions
export const FACILITY_AGENT_PERMISSIONS = {
  ACCESS_DASHBOARD: 'access_dashboard',
  ACCESS_SUBSCRIPTION_MANAGEMENT: 'access_subscription_management',
  ACCESS_CREATE_SUB_AGREEMENT: 'access_create_sub_agreement',
  ACCESS_SUB_AGREEMENT_MANAGEMENT: 'access_sub_agreement_management',
  ACCESS_BOND_APPLICATIONS: 'access_bond_applications',
  ACCESS_BOND_HOLDINGS: 'access_bond_holdings',
  ACCESS_ISSUANCE_PAYMENT_MANAGEMENT: 'access_issuance_payment_management',
  ACCESS_COUPON_MANAGEMENT: 'access_coupon_management',
  ACCESS_MATURITY_MANAGEMENT: 'access_maturity_management',
  ACCESS_TOKEN_HOLDER: 'access_token_holder',
  ACCESS_DOCUMENTS: 'access_documents',
  ACCESS_SUB_ACCOUNTS: 'access_sub_accounts',
  ACCESS_ROLE_MANAGEMENT: 'access_role_management',
  ACCESS_AUDIT_LOGS: 'access_audit_logs'
};

// Primary Subscriber Permissions
export const PRIMARY_SUBSCRIBER_PERMISSIONS = {
  ACCESS_CREATE_LOC: 'access_create_loc',
  ACCESS_LOC_MANAGEMENT: 'access_loc_management',
  ACCESS_SUBMIT_SUB_AGREEMENT: 'access_submit_sub_agreement',
  ACCESS_RECEIVE_BOND_TOKEN: 'access_receive_bond_token',
  ACCESS_BOND_TOKEN_ALLOCATION: 'access_bond_token_allocation',
  ACCESS_BOND_HOLDINGS: 'access_bond_holdings',
  ACCESS_TOKEN_HOLDER: 'access_token_holder',
  ACCESS_DOCUMENTS: 'access_documents',
  ACCESS_SUB_ACCOUNTS: 'access_sub_accounts',
  ACCESS_ROLE_MANAGEMENT: 'access_role_management'
};

// Custodian Permissions
export const CUSTODIAN_PERMISSIONS = {
  ACCESS_DASHBOARD: 'access_dashboard',
  ACCESS_SUB_INVESTORS: 'access_sub_investors',
  ACCESS_SUB_INVESTOR_LOC: 'access_sub_investor_loc',
  ACCESS_RECEIVE_BOND_TOKEN: 'access_receive_bond_token',
  ACCESS_BOND_TOKEN_ALLOCATION: 'access_bond_token_allocation',
  ACCESS_BOND_HOLDINGS: 'access_bond_holdings',
  ACCESS_COUPON_MANAGEMENT: 'access_coupon_management',
  ACCESS_MATURITY_MANAGEMENT: 'access_maturity_management',
  ACCESS_WALLET_MANAGEMENT: 'access_wallet_management',
  ACCESS_DOCUMENTS: 'access_documents',
  ACCESS_ANNOUNCEMENTS: 'access_announcements',
  ACCESS_ROLE_MANAGEMENT: 'access_role_management',
  ACCESS_SUB_ACCOUNTS: 'access_sub_accounts',
  ACCESS_REPORTS: 'access_reports'
};

// Super Admin Permissions
export const SUPER_ADMIN_PERMISSIONS = {
  ACCESS_DASHBOARD: 'access_dashboard',
  ACCESS_USER_MANAGEMENT: 'access_user_management',
  ACCESS_SUB_ACCOUNTS: 'access_sub_accounts',
  ACCESS_ROLE_MANAGEMENT: 'access_role_management',
  ACCESS_DOCUMENTS: 'access_documents'
};

// Investor Permissions
export const INVESTOR_PERMISSIONS = {
  ACCESS_DASHBOARD: 'access_dashboard',
  ACCESS_SUBMIT_LOC: 'access_submit_loc',
  ACCESS_RECEIVE_BOND_TOKEN: 'access_receive_bond_token',
  ACCESS_BOND_HOLDINGS: 'access_bond_holdings',
  ACCESS_COUPON_MANAGEMENT: 'access_coupon_management',
  ACCESS_MATURITY_MANAGEMENT: 'access_maturity_management',
  ACCESS_WALLET_MANAGEMENT: 'access_wallet_management',
  ACCESS_SUB_ACCOUNTS: 'access_sub_accounts',
  ACCESS_ROLE_MANAGEMENT: 'access_role_management',
  ACCESS_ANNOUNCEMENTS: 'access_announcements',
  ACCESS_DOCUMENTS: 'access_documents',
  ACCESS_REPORTS: 'access_reports'
};

// Trustee Permissions
export const TRUSTEE_PERMISSIONS = {
  ACCESS_DASHBOARD: 'access_dashboard',
  ACCESS_ASSIGNED_BONDS: 'access_assigned_bonds',
  ACCESS_BOND_HOLDERS: 'access_bond_holders',
  ACCESS_DOCUMENTS: 'access_documents',
  ACCESS_ANNOUNCEMENTS: 'access_announcements',
  ACCESS_ROLE_MANAGEMENT: 'access_role_management',
  ACCESS_SUB_ACCOUNTS: 'access_sub_accounts'
};

// Page access permissions mapping
export const PAGE_PERMISSIONS = {
  // =============================================================================
  // INVESTOR PAGES
  // =============================================================================
  '/investor': [INVESTOR_PERMISSIONS.ACCESS_DASHBOARD],
  '/investor/subscription-requests': [INVESTOR_PERMISSIONS.ACCESS_SUBMIT_LOC],
  '/investor/bond-token-requests': [INVESTOR_PERMISSIONS.ACCESS_RECEIVE_BOND_TOKEN],
  '/investor/holdings': [INVESTOR_PERMISSIONS.ACCESS_BOND_HOLDINGS],
  '/investor/payouts': [INVESTOR_PERMISSIONS.ACCESS_COUPON_MANAGEMENT],
  '/investor/coupon-distribution-requests': [INVESTOR_PERMISSIONS.ACCESS_COUPON_MANAGEMENT],
  '/investor/maturity-management': [INVESTOR_PERMISSIONS.ACCESS_MATURITY_MANAGEMENT],
  '/investor/wallet': [INVESTOR_PERMISSIONS.ACCESS_WALLET_MANAGEMENT],
  '/investor/sub-accounts': [INVESTOR_PERMISSIONS.ACCESS_SUB_ACCOUNTS],
  '/investor/roles': [INVESTOR_PERMISSIONS.ACCESS_ROLE_MANAGEMENT],
  '/investor/announcements': [INVESTOR_PERMISSIONS.ACCESS_ANNOUNCEMENTS],
  '/investor/documents': [INVESTOR_PERMISSIONS.ACCESS_DOCUMENTS],
  '/investor/reports': [INVESTOR_PERMISSIONS.ACCESS_REPORTS],

  // =============================================================================
  // CUSTODIAN PAGES
  // =============================================================================
  '/custodian': [CUSTODIAN_PERMISSIONS.ACCESS_DASHBOARD],
  '/custodian/sub-investors': [CUSTODIAN_PERMISSIONS.ACCESS_SUB_INVESTORS],
  '/custodian/kyc-review': [CUSTODIAN_PERMISSIONS.ACCESS_SUB_INVESTORS],
  '/custodian/loc-requests': [CUSTODIAN_PERMISSIONS.ACCESS_SUB_INVESTOR_LOC],
  '/custodian/bond-token-requests': [CUSTODIAN_PERMISSIONS.ACCESS_RECEIVE_BOND_TOKEN],
  '/custodian/bond-token-allocation': [CUSTODIAN_PERMISSIONS.ACCESS_BOND_TOKEN_ALLOCATION],
  '/custodian/bond-holdings': [CUSTODIAN_PERMISSIONS.ACCESS_BOND_HOLDINGS],
  '/custodian/bond-holders': [CUSTODIAN_PERMISSIONS.ACCESS_BOND_HOLDINGS],
  '/custodian/coupon-profit-distribution': [CUSTODIAN_PERMISSIONS.ACCESS_COUPON_MANAGEMENT],
  '/custodian/maturity-management': [CUSTODIAN_PERMISSIONS.ACCESS_MATURITY_MANAGEMENT],
  '/custodian/wallets': [CUSTODIAN_PERMISSIONS.ACCESS_WALLET_MANAGEMENT],
  '/custodian/wallet-management': [CUSTODIAN_PERMISSIONS.ACCESS_WALLET_MANAGEMENT],
  '/custodian/sub-accounts': [CUSTODIAN_PERMISSIONS.ACCESS_SUB_ACCOUNTS],
  '/custodian/roles': [CUSTODIAN_PERMISSIONS.ACCESS_ROLE_MANAGEMENT],
  '/custodian/announcements': [CUSTODIAN_PERMISSIONS.ACCESS_ANNOUNCEMENTS],
  '/custodian/documents': [CUSTODIAN_PERMISSIONS.ACCESS_DOCUMENTS],
  '/custodian/reports': [CUSTODIAN_PERMISSIONS.ACCESS_REPORTS],
  '/custodian/client-transfers': [], // Accessible to all custodian accounts

  // =============================================================================
  // FACILITY AGENT PAGES
  // =============================================================================
  '/facility-agent': [FACILITY_AGENT_PERMISSIONS.ACCESS_DASHBOARD],
  '/facility-agent/subscription': [FACILITY_AGENT_PERMISSIONS.ACCESS_SUBSCRIPTION_MANAGEMENT],
  '/facility-agent/create-sub-agreement': [FACILITY_AGENT_PERMISSIONS.ACCESS_CREATE_SUB_AGREEMENT],
  '/facility-agent/sub-agreement-management': [FACILITY_AGENT_PERMISSIONS.ACCESS_SUB_AGREEMENT_MANAGEMENT],
  '/facility-agent/applications': [FACILITY_AGENT_PERMISSIONS.ACCESS_BOND_APPLICATIONS],
  '/facility-agent/bond-holdings': [FACILITY_AGENT_PERMISSIONS.ACCESS_BOND_HOLDINGS],
  '/facility-agent/bond-holders/:bondId': [FACILITY_AGENT_PERMISSIONS.ACCESS_BOND_HOLDINGS],
  '/facility-agent/issuance-payment-management': [FACILITY_AGENT_PERMISSIONS.ACCESS_ISSUANCE_PAYMENT_MANAGEMENT],
  '/facility-agent/coupon-profit-distribution': [FACILITY_AGENT_PERMISSIONS.ACCESS_COUPON_MANAGEMENT],
  '/facility-agent/maturity-management': [FACILITY_AGENT_PERMISSIONS.ACCESS_MATURITY_MANAGEMENT],
  '/facility-agent/token-holder-register': [FACILITY_AGENT_PERMISSIONS.ACCESS_TOKEN_HOLDER],
  '/facility-agent/registry': [FACILITY_AGENT_PERMISSIONS.ACCESS_TOKEN_HOLDER],
  '/facility-agent/sub-accounts': [FACILITY_AGENT_PERMISSIONS.ACCESS_SUB_ACCOUNTS],
  '/facility-agent/roles': [FACILITY_AGENT_PERMISSIONS.ACCESS_ROLE_MANAGEMENT],
  '/facility-agent/documents': [FACILITY_AGENT_PERMISSIONS.ACCESS_DOCUMENTS],
  '/facility-agent/reports': [FACILITY_AGENT_PERMISSIONS.ACCESS_AUDIT_LOGS],

  // =============================================================================
  // PRIMARY SUBSCRIBER PAGES
  // =============================================================================
  '/primary-subscriber/create-loc': [PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_CREATE_LOC],
  '/primary-subscriber/loc-management': [PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_LOC_MANAGEMENT],
  '/primary-subscriber/submit-sub-agreement': [PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_SUBMIT_SUB_AGREEMENT],
  '/primary-subscriber/bond-token-requests': [PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_RECEIVE_BOND_TOKEN],
  '/primary-subscriber/bond-token-allocation': [PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_BOND_TOKEN_ALLOCATION],
  '/primary-subscriber/bond-holdings': [PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_BOND_HOLDINGS],
  '/primary-subscriber/token-holder-register': [PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_TOKEN_HOLDER],
  '/primary-subscriber/sub-accounts': [PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_SUB_ACCOUNTS],
  '/primary-subscriber/roles': [PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_ROLE_MANAGEMENT],
  '/primary-subscriber/documents': [PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_DOCUMENTS],

  // =============================================================================
  // ISSUER PAGES
  // =============================================================================
  '/issuer': [ISSUER_PERMISSIONS.ACCESS_DASHBOARD],
  '/issuer/bonds/create': [ISSUER_PERMISSIONS.ACCESS_CREATE_BOND],
  '/issuer/bonds': [ISSUER_PERMISSIONS.ACCESS_BOND_MANAGEMENT],
  '/issuer/bonds/:bondId': [ISSUER_PERMISSIONS.ACCESS_BOND_MANAGEMENT],
  '/issuer/announcements': [ISSUER_PERMISSIONS.ACCESS_ANNOUNCEMENTS],
  '/issuer/sub-accounts': [ISSUER_PERMISSIONS.ACCESS_SUB_ACCOUNTS],
  '/issuer/roles': [ISSUER_PERMISSIONS.ACCESS_ROLE_MANAGEMENT],
  '/issuer/audit-logs': [ISSUER_PERMISSIONS.ACCESS_AUDIT_LOGS],
  '/issuer/documents': [ISSUER_PERMISSIONS.ACCESS_DOCUMENTS],

  // =============================================================================
  // SUPER ADMIN PAGES
  // =============================================================================
  '/admin': [], // Dashboard - no permission required, always accessible
  '/admin/users': [SUPER_ADMIN_PERMISSIONS.ACCESS_USER_MANAGEMENT],
  '/admin/users/create': [SUPER_ADMIN_PERMISSIONS.ACCESS_USER_MANAGEMENT],
  '/admin/sub-accounts': [SUPER_ADMIN_PERMISSIONS.ACCESS_SUB_ACCOUNTS],
  '/admin/roles': [SUPER_ADMIN_PERMISSIONS.ACCESS_ROLE_MANAGEMENT],
  '/admin/documents': [SUPER_ADMIN_PERMISSIONS.ACCESS_DOCUMENTS],

  // =============================================================================
  // TRUSTEE PAGES
  // =============================================================================
  '/trustee': [TRUSTEE_PERMISSIONS.ACCESS_DASHBOARD],
  '/trustee/bonds': [TRUSTEE_PERMISSIONS.ACCESS_ASSIGNED_BONDS],
  '/trustee/holders': [TRUSTEE_PERMISSIONS.ACCESS_BOND_HOLDERS],
  '/trustee/documents': [TRUSTEE_PERMISSIONS.ACCESS_DOCUMENTS],
  '/trustee/announcements': [TRUSTEE_PERMISSIONS.ACCESS_ANNOUNCEMENTS],
  '/trustee/roles': [TRUSTEE_PERMISSIONS.ACCESS_ROLE_MANAGEMENT],
  '/trustee/subaccounts': [TRUSTEE_PERMISSIONS.ACCESS_SUB_ACCOUNTS]
};

// Route to permission mapping for sidebar filtering
export const ROUTE_PERMISSION_MAP = {
  // Investor routes
  '/investor': INVESTOR_PERMISSIONS.ACCESS_DASHBOARD,
  '/investor/subscription-requests': INVESTOR_PERMISSIONS.ACCESS_SUBMIT_LOC,
  '/investor/bond-token-requests': INVESTOR_PERMISSIONS.ACCESS_RECEIVE_BOND_TOKEN,
  '/investor/holdings': INVESTOR_PERMISSIONS.ACCESS_BOND_HOLDINGS,
  '/investor/payouts': INVESTOR_PERMISSIONS.ACCESS_COUPON_MANAGEMENT,
  '/investor/maturity-management': INVESTOR_PERMISSIONS.ACCESS_MATURITY_MANAGEMENT,
  '/investor/wallet': INVESTOR_PERMISSIONS.ACCESS_WALLET_MANAGEMENT,
  '/investor/sub-accounts': INVESTOR_PERMISSIONS.ACCESS_SUB_ACCOUNTS,
  '/investor/roles': INVESTOR_PERMISSIONS.ACCESS_ROLE_MANAGEMENT,
  '/investor/announcements': INVESTOR_PERMISSIONS.ACCESS_ANNOUNCEMENTS,
  '/investor/documents': INVESTOR_PERMISSIONS.ACCESS_DOCUMENTS,
  '/investor/reports': INVESTOR_PERMISSIONS.ACCESS_REPORTS,

  // Custodian routes
  '/custodian': CUSTODIAN_PERMISSIONS.ACCESS_DASHBOARD,
  '/custodian/sub-investors': CUSTODIAN_PERMISSIONS.ACCESS_SUB_INVESTORS,
  '/custodian/loc-requests': CUSTODIAN_PERMISSIONS.ACCESS_SUB_INVESTOR_LOC,
  '/investor/bond-token-requests': CUSTODIAN_PERMISSIONS.ACCESS_RECEIVE_BOND_TOKEN,
  '/custodian/bond-token-allocation': CUSTODIAN_PERMISSIONS.ACCESS_BOND_TOKEN_ALLOCATION,
  '/custodian/bond-holdings': CUSTODIAN_PERMISSIONS.ACCESS_BOND_HOLDINGS,
  '/custodian/coupon-profit-distribution': CUSTODIAN_PERMISSIONS.ACCESS_COUPON_MANAGEMENT,
  '/custodian/maturity-management': CUSTODIAN_PERMISSIONS.ACCESS_MATURITY_MANAGEMENT,
  '/custodian/wallets': CUSTODIAN_PERMISSIONS.ACCESS_WALLET_MANAGEMENT,
  '/custodian/sub-accounts': CUSTODIAN_PERMISSIONS.ACCESS_SUB_ACCOUNTS,
  '/custodian/roles': CUSTODIAN_PERMISSIONS.ACCESS_ROLE_MANAGEMENT,
  '/custodian/announcements': CUSTODIAN_PERMISSIONS.ACCESS_ANNOUNCEMENTS,
  '/custodian/documents': CUSTODIAN_PERMISSIONS.ACCESS_DOCUMENTS,
  '/custodian/reports': CUSTODIAN_PERMISSIONS.ACCESS_REPORTS,

  // Facility Agent routes
  '/facility-agent': FACILITY_AGENT_PERMISSIONS.ACCESS_DASHBOARD,
  '/facility-agent/subscription': FACILITY_AGENT_PERMISSIONS.ACCESS_SUBSCRIPTION_MANAGEMENT,
  '/facility-agent/create-sub-agreement': FACILITY_AGENT_PERMISSIONS.ACCESS_CREATE_SUB_AGREEMENT,
  '/facility-agent/sub-agreement-management': FACILITY_AGENT_PERMISSIONS.ACCESS_SUB_AGREEMENT_MANAGEMENT,
  '/facility-agent/applications': FACILITY_AGENT_PERMISSIONS.ACCESS_BOND_APPLICATIONS,
  '/facility-agent/bond-holdings': FACILITY_AGENT_PERMISSIONS.ACCESS_BOND_HOLDINGS,
  '/facility-agent/issuance-payment-management': FACILITY_AGENT_PERMISSIONS.ACCESS_ISSUANCE_PAYMENT_MANAGEMENT,
  '/facility-agent/coupon-profit-distribution': FACILITY_AGENT_PERMISSIONS.ACCESS_COUPON_MANAGEMENT,
  '/facility-agent/maturity-management': FACILITY_AGENT_PERMISSIONS.ACCESS_MATURITY_MANAGEMENT,
  '/facility-agent/token-holder-register': FACILITY_AGENT_PERMISSIONS.ACCESS_TOKEN_HOLDER,
  '/facility-agent/sub-accounts': FACILITY_AGENT_PERMISSIONS.ACCESS_SUB_ACCOUNTS,
  '/facility-agent/roles': FACILITY_AGENT_PERMISSIONS.ACCESS_ROLE_MANAGEMENT,
  '/facility-agent/documents': FACILITY_AGENT_PERMISSIONS.ACCESS_DOCUMENTS,
  '/facility-agent/reports': FACILITY_AGENT_PERMISSIONS.ACCESS_AUDIT_LOGS,

  // Primary Subscriber routes
  '/primary-subscriber/create-loc': PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_CREATE_LOC,
  '/primary-subscriber/loc-management': PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_LOC_MANAGEMENT,
  '/primary-subscriber/submit-sub-agreement': PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_SUBMIT_SUB_AGREEMENT,
  '/primary-subscriber/bond-token-requests': PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_RECEIVE_BOND_TOKEN,
  '/primary-subscriber/bond-token-allocation': PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_BOND_TOKEN_ALLOCATION,
  '/primary-subscriber/bond-holdings': PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_BOND_HOLDINGS,
  '/primary-subscriber/token-holder-register': PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_TOKEN_HOLDER,
  '/primary-subscriber/sub-accounts': PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_SUB_ACCOUNTS,
  '/primary-subscriber/roles': PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_ROLE_MANAGEMENT,
  '/primary-subscriber/documents': PRIMARY_SUBSCRIBER_PERMISSIONS.ACCESS_DOCUMENTS,

  // Issuer routes
  '/issuer': ISSUER_PERMISSIONS.ACCESS_DASHBOARD,
  '/issuer/bonds/create': ISSUER_PERMISSIONS.ACCESS_CREATE_BOND,
  '/issuer/bonds': ISSUER_PERMISSIONS.ACCESS_BOND_MANAGEMENT,
  '/issuer/announcements': ISSUER_PERMISSIONS.ACCESS_ANNOUNCEMENTS,
  '/issuer/sub-accounts': ISSUER_PERMISSIONS.ACCESS_SUB_ACCOUNTS,
  '/issuer/roles': ISSUER_PERMISSIONS.ACCESS_ROLE_MANAGEMENT,
  '/issuer/audit-logs': ISSUER_PERMISSIONS.ACCESS_AUDIT_LOGS,
  '/issuer/documents': ISSUER_PERMISSIONS.ACCESS_DOCUMENTS,

  // Super Admin routes
  '/admin': null, // Dashboard always accessible
  '/admin/users': SUPER_ADMIN_PERMISSIONS.ACCESS_USER_MANAGEMENT,
  '/admin/sub-accounts': SUPER_ADMIN_PERMISSIONS.ACCESS_SUB_ACCOUNTS,
  '/admin/roles': SUPER_ADMIN_PERMISSIONS.ACCESS_ROLE_MANAGEMENT,
  '/admin/documents': SUPER_ADMIN_PERMISSIONS.ACCESS_DOCUMENTS,

  // Trustee routes
  '/trustee': TRUSTEE_PERMISSIONS.ACCESS_DASHBOARD,
  '/trustee/bonds': TRUSTEE_PERMISSIONS.ACCESS_ASSIGNED_BONDS,
  '/trustee/holders': TRUSTEE_PERMISSIONS.ACCESS_BOND_HOLDERS,
  '/trustee/documents': TRUSTEE_PERMISSIONS.ACCESS_DOCUMENTS,
  '/trustee/announcements': TRUSTEE_PERMISSIONS.ACCESS_ANNOUNCEMENTS,
  '/trustee/roles': TRUSTEE_PERMISSIONS.ACCESS_ROLE_MANAGEMENT,
  '/trustee/subaccounts': TRUSTEE_PERMISSIONS.ACCESS_SUB_ACCOUNTS
};

// Permission checking utility
export const usePermissions = () => {
  const { user } = useAuth();

  // Fetch assigned role data for sub-accounts
  const { data: assignedRole } = useQuery({
    queryKey: ['assigned-role', user?.assignedRoleId, user?.assignedRoleModel],
    queryFn: async () => {
      if (!user?.isSubAccount || !user?.assignedRoleId) {
        return null;
      }

      // If assignedRoleId is already the full role object (has _id and permissions)
      if (user.assignedRoleId && typeof user.assignedRoleId === 'object' && user.assignedRoleId.permissions) {
        return user.assignedRoleId;
      }

      // If assignedRoleId is just an ID, fetch the role data
      if (!user?.assignedRoleModel) {
        return null;
      }

      try {
        let response;
        switch (user.assignedRoleModel) {
          case 'InvestorRole':
            response = await investorRolesAPI.getRole(user.assignedRoleId);
            break;
          case 'CustodianRole':
            response = await custodianRolesAPI.getRoleById(user.assignedRoleId);
            break;
          case 'FacilityAgentRole':
            response = await facilityAgentRolesAPI.getRole(user.assignedRoleId);
            break;
          case 'IssuerRole':
            response = await issuerRolesAPI.getRole(user.assignedRoleId);
            break;
          case 'SuperAdminRole':
            response = await superAdminRolesAPI.getRole(user.assignedRoleId);
            break;
          case 'TrusteeRole':
            response = await trusteeRolesAPI.getRoleById(user.assignedRoleId);
            break;
          case 'PrimarySubscriberRole':
            response = await primarySubscriberRolesAPI.getRole(user.assignedRoleId);
            break;
          default:
            return null;
        }

        // Handle different response structures
        const roleData = response?.data?.data || response?.data || response;
        return roleData;
      } catch (error) {
        console.error('Error fetching assigned role:', error);
        return null;
      }
    },
    enabled: !!user?.isSubAccount && !!user?.assignedRoleId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });

  const hasPermission = (permission) => {
    if (!user) return false;

    // Explicitly check if user is a sub-account
    const isSubAccount = user.isSubAccount === true;

    // If user is a sub-account, check their assigned role permissions
    if (isSubAccount && assignedRole?.permissions) {
      return assignedRole.permissions.includes(permission);
    }

    // If user is a sub-account and assignedRoleId has permissions directly
    if (isSubAccount && user.assignedRoleId?.permissions) {
      return user.assignedRoleId.permissions.includes(permission);
    }

    // If user is a sub-account but role data is not accessible, deny access by default
    if (isSubAccount && !assignedRole?.permissions && !user.assignedRoleId?.permissions) {
      console.warn('Sub-account role data not accessible, denying permission:', permission);
      return false;
    }

    // If user is a parent investor, custodian, facility agent, issuer, trustee, or super admin, they have all permissions
    if ((user.role === 'investor' || user.role === 'custodian' || user.role === 'facility_agent' || user.role === 'primary_subscriber' || user.role === 'issuer' || user.role === 'trustee' || user.role === 'super_admin') && !isSubAccount) {
      return true;
    }

    // For other roles, check if they have the permission
    return user.permissions?.includes(permission) || false;
  };

  const hasAnyPermission = (permissions) => {
    return permissions.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = (permissions) => {
    return permissions.every(permission => hasPermission(permission));
  };

  const canAccessPage = (requiredPermissions) => {
    if (!requiredPermissions || requiredPermissions.length === 0) {
      return true;
    }
    return hasAnyPermission(requiredPermissions);
  };

  // Get permission for a specific route (used for sidebar filtering)
  const getPermissionForRoute = (route) => {
    return ROUTE_PERMISSION_MAP[route] || null;
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessPage,
    getPermissionForRoute,
    isSubAccount: user?.isSubAccount === true,
    userRole: user?.role,
    assignedRole
  };
};

// Higher-order component for permission-based rendering
export const withPermission = (WrappedComponent, requiredPermission) => {
  return (props) => {
    const { hasPermission } = usePermissions();

    if (!hasPermission(requiredPermission)) {
      return null;
    }

    return <WrappedComponent {...props} />;
  };
};

// Permission-based button component
export const PermissionButton = ({ permission, children, ...props }) => {
  const { hasPermission } = usePermissions();

  if (!hasPermission(permission)) {
    return null;
  }

  return <button {...props}>{children}</button>;
};

// Permission-based link component
export const PermissionLink = ({ permission, children, ...props }) => {
  const { hasPermission } = usePermissions();

  if (!hasPermission(permission)) {
    return null;
  }

  return <a {...props}>{children}</a>;
};

// Permission-based section component
export const PermissionSection = ({ permission, children, fallback = null }) => {
  const { hasPermission } = usePermissions();

  if (!hasPermission(permission)) {
    return fallback;
  }

  return <>{children}</>;
};
